import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wallet, AlertCircle, CheckCircle2, Clock, Receipt, Loader2,
  GraduationCap, School, Trophy, Tag, Calendar, Download, Printer
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const CATEGORY_META = {
  Tuition:  { icon: GraduationCap, color: 'bg-indigo-50 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500' },
  School:   { icon: School,        color: 'bg-blue-50 text-blue-700 border-blue-200',       dot: 'bg-blue-500' },
  Activity: { icon: Trophy,        color: 'bg-amber-50 text-amber-700 border-amber-200',    dot: 'bg-amber-500' },
  Other:    { icon: Tag,           color: 'bg-gray-50 text-gray-700 border-gray-200',       dot: 'bg-gray-500' },
};

const CategoryBadge = ({ category }) => {
  const meta = CATEGORY_META[category] || CATEGORY_META.Other;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${meta.color}`}>
      <Icon size={12} />
      {category}
    </span>
  );
};

const StudentFeesView = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('pending'); // pending | timeline
  const [receiptModal, setReceiptModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/student/fees/summary`);
      setData(res.data || null);
    } catch (e) {
      toast.error('Could not load your fee details');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openReceipt = async (fee) => {
    try {
      const res = await axios.get(`${API}/student/fees/${fee.id}/receipt`);
      setReceiptModal(res.data);
    } catch (e) {
      toast.error('Could not open receipt');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-12 flex items-center justify-center gap-3 text-gray-500" data-testid="student-fees-loading">
        <Loader2 className="animate-spin" size={20} />
        Loading your fee details…
      </div>
    );
  }

  if (!data || !data.student) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500" data-testid="student-fees-empty">
        No fee information available.
      </div>
    );
  }

  const { totals, by_category: byCategory, instalments, timeline, next_due: nextDue, school_name: schoolName, student } = data;
  const pending = (instalments || []).filter(i => i.status === 'pending');
  const categories = Object.keys(byCategory || {});

  return (
    <div className="space-y-6" data-testid="student-fees-view">
      {/* Top summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-slate-700 to-slate-800 text-white rounded-xl p-5" data-testid="fees-kpi-total">
          <div className="flex items-center gap-2 text-slate-300 text-xs uppercase tracking-wider">
            <Wallet size={14} />
            Total Annual Fee
          </div>
          <div className="text-3xl font-bold mt-2">{fmtINR(totals.total)}</div>
          <div className="text-xs text-slate-300 mt-1">{(instalments || []).length} instalment{(instalments || []).length === 1 ? '' : 's'}</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white rounded-xl p-5" data-testid="fees-kpi-paid">
          <div className="flex items-center gap-2 text-emerald-100 text-xs uppercase tracking-wider">
            <CheckCircle2 size={14} />
            Paid
          </div>
          <div className="text-3xl font-bold mt-2">{fmtINR(totals.paid)}</div>
          <div className="text-xs text-emerald-100 mt-1">
            {totals.total > 0 ? `${Math.round((totals.paid / totals.total) * 100)}% completed` : '—'}
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-xl p-5" data-testid="fees-kpi-balance">
          <div className="flex items-center gap-2 text-amber-100 text-xs uppercase tracking-wider">
            <Clock size={14} />
            Balance
          </div>
          <div className="text-3xl font-bold mt-2">{fmtINR(totals.balance)}</div>
          <div className="text-xs text-amber-100 mt-1">{pending.length} pending</div>
        </div>
        <div className="bg-gradient-to-br from-rose-500 to-red-600 text-white rounded-xl p-5" data-testid="fees-kpi-next-due">
          <div className="flex items-center gap-2 text-rose-100 text-xs uppercase tracking-wider">
            <Calendar size={14} />
            Next Due Date
          </div>
          <div className="text-2xl font-bold mt-2">{nextDue ? fmtDate(nextDue.due_date) : 'All clear ✓'}</div>
          <div className="text-xs text-rose-100 mt-1">{nextDue ? `${fmtINR(nextDue.amount)} · ${nextDue.term_name}` : 'No pending dues'}</div>
        </div>
      </div>

      {/* Category breakdown */}
      {categories.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="fees-category-breakdown">
          <div className="px-6 py-4 border-b bg-gradient-to-r from-purple-600 to-purple-700">
            <h3 className="text-white font-semibold">Fee Categories</h3>
            <p className="text-purple-100 text-xs">Breakdown by Tuition · School · Extra-curricular activities</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 p-6">
            {categories.map(cat => {
              const meta = CATEGORY_META[cat] || CATEGORY_META.Other;
              const Icon = meta.icon;
              const b = byCategory[cat];
              const pct = b.total > 0 ? Math.round((b.paid / b.total) * 100) : 0;
              return (
                <div key={cat} className={`border rounded-lg p-4 ${meta.color}`} data-testid={`fees-category-${cat}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={18} />
                    <span className="font-semibold">{cat}</span>
                    <span className="ml-auto text-xs opacity-75">{b.count} item{b.count === 1 ? '' : 's'}</span>
                  </div>
                  <div className="text-2xl font-bold mb-1">{fmtINR(b.total)}</div>
                  <div className="text-xs opacity-80 mb-2">Paid {fmtINR(b.paid)} · Balance {fmtINR(b.balance)}</div>
                  <div className="w-full h-1.5 bg-white/40 rounded-full overflow-hidden">
                    <div className={`${meta.dot} h-full rounded-full transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="border-b flex">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'pending' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid="fees-tab-pending"
          >
            Pending Dues ({pending.length})
          </button>
          <button
            onClick={() => setActiveTab('timeline')}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'timeline' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid="fees-tab-timeline"
          >
            Payment Timeline &amp; Receipts ({(timeline || []).length})
          </button>
        </div>

        {/* Pending tab */}
        {activeTab === 'pending' && (
          <div className="divide-y" data-testid="fees-pending-list">
            {pending.length === 0 && (
              <div className="p-10 text-center text-gray-500">
                <CheckCircle2 className="mx-auto text-emerald-400 mb-3" size={48} />
                <p className="font-medium text-emerald-700">You&apos;re all caught up!</p>
                <p className="text-sm">No pending dues.</p>
              </div>
            )}
            {pending.map(item => (
              <div key={item.id} className="p-4 flex items-center justify-between gap-4 hover:bg-gray-50" data-testid={`fee-pending-${item.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">{item.term_name}</span>
                    <CategoryBadge category={item.category} />
                    {item.overdue && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-md">
                        <AlertCircle size={12} /> Overdue
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Due {fmtDate(item.due_date)}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xl font-bold text-gray-900">{fmtINR(item.amount)}</div>
                  <div className="text-xs text-gray-400">Pay at school office</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Timeline tab */}
        {activeTab === 'timeline' && (
          <div data-testid="fees-timeline-list">
            {(timeline || []).length === 0 && (
              <div className="p-10 text-center text-gray-500">
                <Receipt className="mx-auto text-gray-300 mb-3" size={48} />
                <p>No receipts yet. Payments will appear here once recorded by the school office.</p>
              </div>
            )}
            <ul className="divide-y">
              {(timeline || []).map(item => (
                <li key={item.id} className="p-4 flex items-center justify-between gap-4 hover:bg-gray-50" data-testid={`fee-receipt-${item.id}`}>
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                      <CheckCircle2 size={18} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">{item.term_name}</span>
                        <CategoryBadge category={item.category} />
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Paid {fmtDate(item.paid_at)} · Receipt {item.receipt_no}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <div className="text-lg font-bold text-emerald-700">{fmtINR(item.amount)}</div>
                    </div>
                    <button
                      onClick={() => openReceipt(item)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-purple-600 text-white text-xs font-semibold hover:bg-purple-700"
                      data-testid={`fee-receipt-btn-${item.id}`}
                    >
                      <Receipt size={14} /> Receipt
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Receipt modal */}
      {receiptModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setReceiptModal(null)}>
          <div className="bg-white rounded-xl max-w-md w-full overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="fee-receipt-modal">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs uppercase tracking-wider">Fee Receipt</p>
                  <p className="font-bold text-lg">{receiptModal.receipt_no}</p>
                </div>
                <button onClick={() => setReceiptModal(null)} className="text-purple-100 hover:text-white text-2xl leading-none">×</button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-center border-b pb-3">
                <h4 className="font-bold text-gray-900">{receiptModal.school_name}</h4>
                <p className="text-xs text-gray-500">Official Fee Receipt</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Student</p>
                  <p className="font-medium">{receiptModal.student_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Class · Section · Roll</p>
                  <p className="font-medium">Class {receiptModal.class_level} · {receiptModal.section} · {receiptModal.roll_number}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Fee Item</p>
                  <p className="font-medium">{receiptModal.term_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Category</p>
                  <p className="font-medium">{receiptModal.category}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Due Date</p>
                  <p className="font-medium">{fmtDate(receiptModal.due_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Paid On</p>
                  <p className="font-medium">{fmtDate(receiptModal.paid_at)}</p>
                </div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center justify-between">
                <span className="text-emerald-800 font-medium">Amount Paid</span>
                <span className="text-2xl font-bold text-emerald-700">{fmtINR(receiptModal.amount)}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => window.print()}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-gray-700 text-white rounded-md text-sm font-semibold hover:bg-gray-800"
                  data-testid="receipt-print-btn"
                >
                  <Printer size={14} /> Print
                </button>
                <button
                  onClick={() => {
                    const blob = new Blob([JSON.stringify(receiptModal, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${receiptModal.receipt_no}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-md text-sm font-semibold hover:bg-purple-700"
                  data-testid="receipt-download-btn"
                >
                  <Download size={14} /> Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentFeesView;
